/**
 * Self-contained build-time feature flag + helpers for the LinkVault
 * faviconless-placeholder fix (ISP-linkvault-favicon-fix).
 *
 * WHY ITS OWN FILE (mirrors LinkedWheels/featureFlag.js, gradientBuilder/
 * featureFlag.js): kept local so it can be reviewed/merged independently of the
 * parallel merge-train branches. Defaults OFF; flipped per environment via a
 * REACT_APP_* build var. At merge this can fold into src/constants/featureFlags.js.
 *
 * THE BUG: a website with no favicon, when queried through DuckDuckGo's
 * `external-content.duckduckgo.com/ip3/<domain>.ico` service, is served DDG's
 * OWN person/silhouette placeholder — as a 200-OK image. Because it loads
 * successfully, the <img> onError->globe fallback never fires, so the LinkVault
 * shows a person instead of the intended internet globe.
 *
 * THE FIX (flag ON): produce the favicon URL via Google's S2 service instead
 * (`google.com/s2/favicons?sz=128&domain=<domain>`). Google serves REAL favicons
 * up to the site's native resolution (measured: github 32px, stripe 128px at
 * sz=128 vs. only 32/64px at the old sz=64), so a larger request downsampled to
 * the ~24px display size stays crisp on 1x AND retina.
 *
 * THE GLOBE-BLUR (why sz alone is not enough — measured 2026-07-15): for a domain
 * with NO favicon, Google returns a FIXED 16x16 raster globe *regardless of sz*,
 * served with HTTP 404 but a DECODABLE body. Browsers PAINT that body and fire
 * `load` (not `error`) — verified in Chrome: onError never fires, so the
 * onError->globe fallback can never catch it and the 16px globe upscales blurry to
 * 24px. handleFaviconLoad() closes this: on `load`, if the raster is <=16px (too
 * low-res to render crisply — the Google globe, or an equally-blurry tiny real
 * favicon) it swaps to the local VECTOR globe (ASSETS.website), which is crisp at
 * any size. Real favicons (>16px) are kept. onError stays as belt-and-suspenders
 * for genuine network failures.
 *
 * Flag OFF (default) is byte-identical to today: getFaviconUrl() returns the exact
 * DDG URL, the onError handler (present on sandbox already) is unchanged, and no
 * onLoad handler is attached (the components gate it on the build flag).
 */
const truthy = (v) => v === "true" || v === "1" || v === true;

export const LINKVAULT_FAVICON_FIX_ENABLED = truthy(
  process.env.REACT_APP_LINKVAULT_FAVICON_FIX
);

/**
 * Requested favicon pixel size for Google S2 (flag ON). 128 (not the old 64) so
 * REAL favicons come back at higher native resolution and stay crisp when the
 * <img> downsamples them to the ~24px display box (incl. 2x/3x retina). Google
 * caps a faviconless domain at a fixed 16px globe regardless of this value — that
 * case is handled by handleFaviconLoad, not by the requested size.
 */
export const FAVICON_S2_SIZE = 128;

/**
 * Favicon-service URL selector. Flag OFF -> DuckDuckGo (exact current string).
 * Flag ON -> Google S2 at FAVICON_S2_SIZE (globe default for faviconless domains).
 * `enabled` is a param (defaulting to the build flag) so it is deterministically
 * unit-testable in both states.
 * @param {string} domain hostname, e.g. "example.com"
 * @param {boolean} [enabled]
 * @returns {string} favicon image URL
 */
export const getFaviconUrl = (domain, enabled = LINKVAULT_FAVICON_FIX_ENABLED) =>
  enabled
    ? `https://www.google.com/s2/favicons?sz=${FAVICON_S2_SIZE}&domain=${domain}`
    : `https://external-content.duckduckgo.com/ip3/${domain}.ico`;

/**
 * Matches a stored DuckDuckGo ip3 favicon URL and captures its domain.
 * DDG shape (the exact string getFaviconUrl produces flag-OFF):
 *   https://external-content.duckduckgo.com/ip3/<domain>.ico
 */
const DDG_IP3_RE =
  /^https:\/\/external-content\.duckduckgo\.com\/ip3\/(.+)\.ico$/;

/**
 * Best-effort `new URL(value)` parse that never throws. Used by
 * `extractFaviconDomain` instead of hand-rolled query-string regexes, so a
 * param appearing in any order (`?domain=x&sz=128` vs `?sz=128&domain=x`) is
 * found the same way (and `searchParams.get` auto-decodes for us).
 * @param {string} value
 * @returns {URL|null}
 */
const parseUrl = (value) => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

/**
 * Resolves a hostname from either a full URL or a bare domain string (the
 * gstatic `url=` param is normally a full page URL, e.g. "https://x.com/",
 * but is parsed defensively in case it is ever just "x.com").
 * @param {string} value
 * @returns {string|null}
 */
const hostnameFromUrlLike = (value) => {
  const direct = parseUrl(value);
  if (direct) return direct.hostname;
  const withScheme = parseUrl(`https://${value}`);
  return withScheme ? withScheme.hostname : null;
};

/**
 * SHAPE-AGNOSTIC domain extraction (ISP-2131 papercut round 2, 2026-07-21):
 * pulls the target domain out of a stored/rendered favicon URL regardless of
 * WHICH producer shape saved it, so the placeholder guard below can catch a
 * placeholder link no matter when it was saved:
 *   - DDG ip3          (pre-#2144 default producer)
 *   - Google S2         (`google.com/s2/favicons?...&domain=<d>...`, #2144 producer)
 *   - gstatic faviconV2 (`t*.gstatic.com/faviconV2?...&url=<page-url>...` — the
 *     CDN response target S2 itself redirects to; LinkVault never persists this
 *     directly today, matched defensively so the guard still holds if that ever
 *     changes)
 * Uses real `URL`/`searchParams` parsing (not a positional regex) so param
 * order never matters. Returns null for anything else (custom upload, data
 * URI, unrecognised host) so callers can no-op safely.
 * @param {string|undefined|null} url
 * @returns {string|null}
 */
export const extractFaviconDomain = (url) => {
  if (!url || typeof url !== "string") return null;

  const ddg = url.match(DDG_IP3_RE);
  if (ddg) return ddg[1];

  const parsed = parseUrl(url);
  if (!parsed) return null;

  if (parsed.hostname === "google.com" || parsed.hostname === "www.google.com") {
    return parsed.pathname === "/s2/favicons"
      ? parsed.searchParams.get("domain")
      : null;
  }

  if (/^t\d*\.gstatic\.com$/.test(parsed.hostname) && parsed.pathname === "/faviconV2") {
    const target = parsed.searchParams.get("url");
    return target ? hostnameFromUrlLike(target) : null;
  }

  return null;
};

/**
 * HEAL-ON-RENDER: rewrite an already-stored favicon URL for DISPLAY only, so
 * links saved BEFORE the producer fix (whose `icon` is still the DDG ip3 URL that
 * serves DDG's person/silhouette placeholder) render the Google S2 globe default
 * without any DB write or manual re-save.
 *
 * PLACEHOLDER GUARD (round 2, unconditional): an ALREADY-SAVED placeholder link
 * (example.com/.org/.net) must fire ZERO favicon-service request on render, in
 * ANY producer shape (DDG, S2, or S2's own gstatic redirect target) and
 * regardless of the build flag — mirrors the write-path guard already applied
 * in SocialLinksCard's `resolveFaviconIcon()` (isPlaceholderDomain), which only
 * protects links saved/edited AFTER that guard shipped. This is what closes the
 * gap for links saved BEFORE it.
 *
 * Flag OFF (default) -> returns `icon` UNCHANGED unless it is a placeholder
 * (byte-identical to today for every ordinary link).
 * Flag ON + `icon` is a DDG ip3 URL -> returns the Google S2 URL for that domain.
 * Flag ON + anything else (falsy, already-S2, custom upload, data URI) -> UNCHANGED.
 *
 * Pure + `enabled` param defaulting to the build flag, so it is deterministically
 * unit-testable in both states. The onError->globe handler stays as belt-and-
 * suspenders for genuine load failures.
 * @param {string|undefined|null} icon stored icon URL (may be empty)
 * @param {boolean} [enabled]
 * @returns {string|undefined|null} icon to render ("" for a placeholder)
 */
export const healFaviconUrl = (icon, enabled = LINKVAULT_FAVICON_FIX_ENABLED) => {
  if (!icon || typeof icon !== "string") return icon;

  const domain = extractFaviconDomain(icon);
  if (domain && isPlaceholderDomain(domain)) return "";

  if (!enabled) return icon;
  const match = icon.match(DDG_IP3_RE);
  if (!match) return icon;
  return getFaviconUrl(match[1], true);
};

/**
 * onError handler factory for a favicon <img>: swap to the globe asset on load
 * failure. Loop-guarded — only swaps when the current src is NOT already the
 * globe, so a globe that itself fails to load can't re-enter infinitely.
 * Behaviourally identical to the prior inline handler; centralised here so it is
 * a single, tested unit shared by every LinkVault favicon <img>.
 * @param {string} globeSrc resolved URL of the globe asset (ASSETS.website)
 * @returns {(event: { target?: HTMLImageElement, currentTarget?: HTMLImageElement }) => void}
 */
export const handleFaviconError = (globeSrc) => (event) => {
  const img = (event && (event.currentTarget || event.target)) || null;
  if (!img) return;
  // Loop guard: if we're already showing the globe, stop (never re-swap).
  if (img.getAttribute("src") === globeSrc) return;
  img.src = globeSrc;
};

/**
 * At/under this rendered pixel width a raster favicon is too low-resolution to
 * display crisply at the LinkVault's ~24px favicon box. Google's faviconless
 * default globe is a FIXED 16x16 PNG (served HTTP 404 with a decodable body, so it
 * paints and fires `load`, never `error`) — as are genuinely tiny 16px favicons,
 * which upscale just as blurrily. Both are replaced with the crisp vector globe.
 */
export const FAVICON_MIN_CRISP_PX = 16;

/**
 * onLoad handler factory for a favicon <img>: when the SUCCESSFULLY-loaded raster
 * is too low-resolution to render crisply (naturalWidth <= FAVICON_MIN_CRISP_PX),
 * swap to the local VECTOR globe (crisp at any size). This is what actually fixes
 * the "blurry globe": Google returns its faviconless globe as a 16x16 body on a
 * 404, which the browser PAINTS (firing `load`, not `error`), so onError can never
 * catch it. Loop-guarded — never re-processes the globe it just swapped in (the
 * vector globe's own naturalWidth is small, so without the guard it would loop).
 * A naturalWidth of 0 (not yet decoded / hard failure) is left for onError.
 * Pure factory; the components attach it only when the build flag is ON, so
 * flag-OFF stays byte-identical (no onLoad handler).
 * @param {string} globeSrc resolved URL of the vector globe asset (ASSETS.website)
 * @param {number} [minPx] resolution threshold (defaults to FAVICON_MIN_CRISP_PX)
 * @returns {(event: { target?: HTMLImageElement, currentTarget?: HTMLImageElement }) => void}
 */
export const handleFaviconLoad =
  (globeSrc, minPx = FAVICON_MIN_CRISP_PX) => (event) => {
    const img = (event && (event.currentTarget || event.target)) || null;
    if (!img) return;
    // Loop guard: never re-process the globe we just swapped in.
    if (img.getAttribute("src") === globeSrc) return;
    const nw = img.naturalWidth;
    if (nw > 0 && nw <= minPx) img.src = globeSrc;
  };

/**
 * PLACEHOLDER-DOMAIN GUARD (ISP-2131 papercut, 2026-07-21): example.com/.org/.net
 * (+ their www. variants) are IANA-reserved placeholder domains (RFC 2606) with
 * no real content or favicon — exactly the shape of a copy-pasted sample link
 * left behind in a LinkVault row (a real saved link on gallery/21's wheel 2643
 * pointed at plain "example.com"). Querying a live favicon service for one of
 * these is a guaranteed-wasted round trip for a domain everyone already knows
 * isn't real. Unconditional (not flag-gated) — always OFF this specific waste,
 * independent of LINKVAULT_FAVICON_FIX_ENABLED, so it applies to both the DDG
 * and the S2 producer.
 * @param {string|undefined|null} domain hostname, e.g. "example.com"
 * @returns {boolean} true if this is a known placeholder domain
 */
const PLACEHOLDER_DOMAINS = new Set([
  "example.com",
  "example.org",
  "example.net",
  "www.example.com",
  "www.example.org",
  "www.example.net",
]);

export const isPlaceholderDomain = (domain) =>
  !!domain && PLACEHOLDER_DOMAINS.has(String(domain).toLowerCase().trim());

/**
 * BARE PLATFORM-KEY ICONS (T22.96, measured on web-stg main.bd2ffb8f.js
 * 2026-10-11): some saved Business Box / LinkVault rows carry `icon` as a bare
 * platform key ("instagram") instead of an image URL. Rendered verbatim, the
 * browser resolves it RELATIVE to the page (/gallery/instagram -> SPA HTML), so
 * the Business Box preview shows a broken image and the editor's onError swaps
 * an instagram.com row to the generic globe. Matches only a single token with no
 * dot, slash or colon — never a valid image URL in its own right.
 */
const BARE_ICON_KEY_RE = /^[A-Za-z][A-Za-z0-9_-]*$/;

/**
 * Display src for a link row's favicon. A bare platform key is resolved from the
 * row's own link domain (same favicon URL a freshly typed row gets); anything
 * else goes through healFaviconUrl unchanged. Returns "" when nothing usable can
 * be derived so call sites fall back to the globe.
 * @param {string|undefined|null} icon stored icon value
 * @param {string|undefined|null} link the row's URL
 * @param {boolean} [enabled]
 * @returns {string|undefined|null}
 */
export const resolveLinkIconSrc = (
  icon,
  link,
  enabled = LINKVAULT_FAVICON_FIX_ENABLED
) => {
  if (typeof icon !== "string" || !BARE_ICON_KEY_RE.test(icon)) {
    return healFaviconUrl(icon, enabled);
  }
  const domain =
    typeof link === "string" && link.trim()
      ? hostnameFromUrlLike(link.trim())
      : null;
  if (!domain || !domain.includes(".") || isPlaceholderDomain(domain)) return "";
  return getFaviconUrl(domain, enabled);
};
